import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  X,
  AlertCircle,
  CheckCircle,
  Search,
  AlertTriangle,
} from "lucide-react";
import { apiService } from "../../services/api";
import { Pipeline, InputDefinition, WorkflowStatus } from "../../types";
import { DynamicWorkflowInputs } from "./DynamicWorkflowInputs";

interface CreateWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workflow?: WorkflowStatus; // If provided, we're editing
  mode?: "create" | "edit";
}

// SWRM-128: Simplified FormData - removed workflow-level fields moved to Workspace Processor
interface FormData {
  name: string;
  priority: "high" | "medium" | "low";
  pipelineId: number | null;
  groupId: string | null;
}

interface UserGroup {
  id: string;
  name: string;
}

// SWRM-128: All processor inputs stored separately
interface ProcessorInputData {
  definitions: InputDefinition[];
  values: Record<string, any>;
  errors: Record<string, string>;
}

interface CreationStep {
  id: string;
  label: string;
  status: "pending" | "in_progress" | "completed" | "error";
}

export const CreateWorkflowModal: React.FC<CreateWorkflowModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  workflow,
  mode = workflow ? "edit" : "create",
}) => {
  const isEditMode = mode === "edit";

  const [formData, setFormData] = useState<FormData>({
    name: "",
    priority: "medium",
    pipelineId: null,
    groupId: null,
  });

  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [creationSteps, setCreationSteps] = useState<CreationStep[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showWarning, setShowWarning] = useState(false);

  // Pipeline selector state (SWRM-66)
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loadingPipelines, setLoadingPipelines] = useState(false);
  const [pipelinesError, setPipelinesError] = useState<string | null>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(
    null
  );
  const [pipelineSearchQuery, setPipelineSearchQuery] = useState("");

  // SWRM-128: All processor inputs (from ALL steps with input_definitions, not just InputProcessor)
  const [allProcessorInputs, setAllProcessorInputs] = useState<{
    [processorName: string]: ProcessorInputData;
  }>({});

  const resetForm = useCallback(() => {
    setFormData({
      name: "",
      priority: "medium",
      pipelineId: null,
      groupId: null,
    });
    setErrors({});
    setCreationSteps([]);
    setIsCreating(false);
    setAllProcessorInputs({});
    setSelectedPipeline(null);
    setPipelineSearchQuery("");
  }, []);

  // Filter and sort pipelines alphabetically
  const filteredPipelines = useMemo(() => {
    const filtered = pipelineSearchQuery
      ? pipelines.filter(
          (pipeline) =>
            pipeline.name
              .toLowerCase()
              .includes(pipelineSearchQuery.toLowerCase()) ||
            (pipeline.description?.toLowerCase() || "").includes(
              pipelineSearchQuery.toLowerCase()
            )
        )
      : pipelines;

    // Sort alphabetically by name
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [pipelines, pipelineSearchQuery]);

  const handleClose = useCallback(() => {
    if (!isCreating) {
      resetForm();
      onClose();
    }
  }, [isCreating, resetForm, onClose]);

  // SWRM-128: Fetch pipelines when modal opens (no default selection)
  // ADR-017: Also fetch user's groups for workflow scoping
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;

      try {
        setLoadingPipelines(true);
        setPipelinesError(null);

        const [pipelineData, groupData] = await Promise.all([
          apiService.getPipelines({ status: "ACTIVE" }),
          apiService.getMyGroups(),
        ]);
        setPipelines(pipelineData);
        setUserGroups(groupData);

        // Auto-select group if user is in exactly one
        if (groupData.length === 1) {
          setFormData((prev) => ({ ...prev, groupId: groupData[0].id }));
        }
      } catch (error) {
        console.error("Failed to fetch pipelines:", error);
        setPipelinesError("Failed to load pipelines. Please try again.");
      } finally {
        setLoadingPipelines(false);
      }
    };

    fetchData();
  }, [isOpen]);

  // Pre-populate form when editing
  useEffect(() => {
    if (!isOpen || !isEditMode || !workflow) return;

    console.log("[CreateWorkflowModal] Edit mode - populating form:", workflow);
    console.log("[CreateWorkflowModal] workflow.inputs:", workflow.inputs);

    // Set basic workflow info
    setFormData({
      name: workflow.name || "",
      priority: (workflow.priority as "high" | "medium" | "low") || "medium",
      pipelineId: workflow.pipeline_id || null,
      groupId: null,
    });

    // Note: workflow.inputs will be populated into allProcessorInputs
    // by the fetchPipelineInputs effect when pipelineId is set
  }, [isOpen, isEditMode, workflow]);

  // SWRM-128: Fetch pipeline details and extract ALL processor input_definitions
  useEffect(() => {
    const fetchPipelineInputs = async () => {
      if (!formData.pipelineId) {
        setAllProcessorInputs({});
        setSelectedPipeline(null);
        return;
      }

      try {
        const pipeline = await apiService.getPipeline(formData.pipelineId);
        setSelectedPipeline(pipeline);

        const processorInputs: { [key: string]: ProcessorInputData } = {};

        // Extract input_definitions from ALL steps (InputProcessor, Workspace, etc.)
        // SWRM-128: InputProcessor uses 'inputs', other processors use 'input_definitions'
        pipeline.steps.forEach((step) => {
          const inputDefs = (step.config?.inputs ||
            step.config?.input_definitions) as InputDefinition[] | undefined;

          console.log(`[CreateWorkflowModal] Step ${step.processor_name}:`, {
            hasInputs: !!step.config?.inputs,
            hasInputDefinitions: !!step.config?.input_definitions,
            inputDefs,
          });

          if (inputDefs && inputDefs.length > 0) {
            const initialValues: Record<string, any> = {};

            // Helper to parse select format and extract default value
            const parseSelectDefault = (formatString: string): string => {
              const parts = formatString.split("|").filter((s) => s.trim());
              const defaultValue =
                parts
                  .find((p) => p.startsWith("*"))
                  ?.replace(/^\*/, "")
                  .trim() || "";
              return defaultValue;
            };

            // Initialize with default values or workflow.inputs (edit mode)
            inputDefs.forEach((input) => {
              // In edit mode, try to get value from workflow.inputs first
              if (
                isEditMode &&
                workflow?.inputs &&
                input.name in workflow.inputs
              ) {
                console.log(
                  `[CreateWorkflowModal] Populating ${input.name} from workflow.inputs:`,
                  workflow.inputs[input.name]
                );
                initialValues[input.name] = workflow.inputs[input.name];
              } else if (
                input.default_value !== undefined &&
                input.default_value !== null
              ) {
                // For select inputs, parse the format to extract the default value
                if (input.display_type === "select") {
                  initialValues[input.name] = parseSelectDefault(
                    input.default_value
                  );
                } else {
                  initialValues[input.name] = input.default_value;
                }
              } else if (input.type === "list" || input.type === "file") {
                initialValues[input.name] = []; // Initialize file/list inputs as empty arrays
              }
            });

            processorInputs[step.processor_name] = {
              definitions: inputDefs,
              values: initialValues,
              errors: {},
            };
          }
        });

        setAllProcessorInputs(processorInputs);
      } catch (error) {
        console.error("Failed to fetch pipeline inputs:", error);
        setAllProcessorInputs({});
        setSelectedPipeline(null);
      }
    };

    fetchPipelineInputs();
  }, [formData.pipelineId, isEditMode, workflow]);

  // SWRM-128: Validate workflow config and ALL processor inputs
  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate workflow-level fields
    if (!formData.name.trim()) {
      newErrors.name = "Workflow name is required";
    }

    if (formData.pipelineId === null) {
      newErrors.pipelineId = "Please select a pipeline";
    }

    setErrors(newErrors);

    // Validate all processor inputs
    let hasInputErrors = false;
    const updatedProcessorInputs = { ...allProcessorInputs };

    Object.keys(allProcessorInputs).forEach((processorName) => {
      const processorData = allProcessorInputs[processorName];
      const inputErrors: Record<string, string> = {};

      processorData.definitions.forEach((inputDef) => {
        const value = processorData.values[inputDef.name];

        // Check if value is provided
        const hasValue =
          value !== undefined &&
          value !== null &&
          (Array.isArray(value)
            ? value.length > 0
            : String(value).trim() !== "");

        // Determine if required (required flag and no default value)
        const isRequired =
          inputDef.required &&
          (inputDef.default_value === undefined ||
            inputDef.default_value === null);

        if (isRequired && !hasValue) {
          inputErrors[inputDef.name] = `${inputDef.name} is required`;
          hasInputErrors = true;
        }
      });

      updatedProcessorInputs[processorName] = {
        ...processorData,
        errors: inputErrors,
      };
    });

    setAllProcessorInputs(updatedProcessorInputs);

    return Object.keys(newErrors).length === 0 && !hasInputErrors;
  }, [formData, allProcessorInputs]);

  const handleInputChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors]
  );

  const updateCreationStep = useCallback(
    (stepId: string, status: CreationStep["status"]) => {
      setCreationSteps((prev) =>
        prev.map((step) => (step.id === stepId ? { ...step, status } : step))
      );
    },
    []
  );

  const performSave = useCallback(async () => {
    // Initialize creation steps
    const steps: CreationStep[] = isEditMode
      ? [
          { id: "validate", label: "Validating changes", status: "pending" },
          { id: "save", label: "Saving workflow", status: "pending" },
          { id: "reset", label: "Resetting workflow", status: "pending" },
        ]
      : [
          {
            id: "validate",
            label: "Validating workflow data",
            status: "pending",
          },
          {
            id: "create_repo",
            label: "Creating repository",
            status: "pending",
          },
          {
            id: "upload_files",
            label: "Uploading specification files",
            status: "pending",
          },
          {
            id: "finalize",
            label: "Finalizing workflow setup",
            status: "pending",
          },
        ];
    setCreationSteps(steps);

    try {
      // Step 1: Validation
      updateCreationStep("validate", "in_progress");

      // SWRM-128: Collect ALL processor inputs into a single flat object
      const workflowInputs: Record<string, any> = {};
      Object.keys(allProcessorInputs).forEach((processorName) => {
        const processorData = allProcessorInputs[processorName];
        Object.assign(workflowInputs, processorData.values);
      });

      updateCreationStep("validate", "completed");

      if (isEditMode && workflow) {
        // EDIT MODE: Update existing workflow
        updateCreationStep("save", "in_progress");

        // Extract NEW files (those with content) and DELETED files
        const newFiles: Record<string, any[]> = {};
        const deleteFiles: Record<string, string[]> = {};
        const scalarInputs: Record<string, any> = {};

        Object.entries(workflowInputs).forEach(([inputName, value]) => {
          if (
            Array.isArray(value) &&
            value.length > 0 &&
            value[0].id &&
            value[0].name
          ) {
            // This is a file/list input
            const filesWithContent = value.filter((f) => f.content);

            if (filesWithContent.length > 0) {
              newFiles[inputName] = filesWithContent;
            }

            // Detect deletions by comparing with original workflow
            const originalFiles = workflow.inputs?.[inputName] || [];
            const currentFilenames = new Set(value.map((f: any) => f.name));
            const deletedFilenames = originalFiles
              .filter((f: any) => !currentFilenames.has(f.name))
              .map((f: any) => f.name);

            if (deletedFilenames.length > 0) {
              deleteFiles[inputName] = deletedFilenames;
            }
          } else {
            // Scalar input (not file/list)
            scalarInputs[inputName] = value;
          }
        });

        // Send to backend
        await apiService.updateWorkflow(workflow.id.toString(), {
          name: formData.name.trim(),
          workflow_inputs: JSON.stringify(scalarInputs),
          new_files:
            Object.keys(newFiles).length > 0
              ? JSON.stringify(newFiles)
              : undefined,
          delete_files:
            Object.keys(deleteFiles).length > 0
              ? JSON.stringify(deleteFiles)
              : undefined,
        });

        updateCreationStep("save", "completed");

        if (Object.keys(newFiles).length > 0) {
          updateCreationStep("upload_files", "completed");
        }

        updateCreationStep("reset", "in_progress");

        // Reset the workflow to apply changes
        await apiService.resetWorkflow(workflow.id.toString());

        updateCreationStep("reset", "completed");
      } else {
        // CREATE MODE: Create new workflow
        updateCreationStep("create_repo", "in_progress");

        // Prepare form data
        // All file content is already embedded in workflowInputs
        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.name.trim());
        formDataToSend.append("priority", formData.priority);

        // Pipeline ID is required
        if (formData.pipelineId !== null) {
          formDataToSend.append("pipeline_id", formData.pipelineId.toString());
        }

        // SWRM-128: Package all processor inputs (including files with content)
        formDataToSend.append(
          "workflow_inputs",
          JSON.stringify(workflowInputs)
        );

        // ADR-017: Include group scope for workflow isolation
        if (formData.groupId) {
          formDataToSend.append("group_id", formData.groupId);
        }

        // Submit to API
        await apiService.createWorkflow(formDataToSend);

        updateCreationStep("create_repo", "completed");
        updateCreationStep("upload_files", "completed");
        updateCreationStep("finalize", "in_progress");

        // Brief delay to show completion
        await new Promise((resolve) => setTimeout(resolve, 500));

        updateCreationStep("finalize", "completed");
      }

      // Success - close modal and refresh dashboard
      setTimeout(() => {
        resetForm();
        onSuccess();
        onClose();
      }, 1000);
    } catch (error) {
      console.error(
        `Workflow ${isEditMode ? "update" : "creation"} failed:`,
        error
      );

      // Mark current step as error
      const currentStep = creationSteps.find(
        (step) => step.status === "in_progress"
      );
      if (currentStep) {
        updateCreationStep(currentStep.id, "error");
      }

      // Show error message
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} workflow`;
      setErrors((prev) => ({ ...prev, submit: errorMessage }));

      setIsCreating(false);
    }
  }, [
    isEditMode,
    workflow,
    formData,
    allProcessorInputs,
    updateCreationStep,
    creationSteps,
    resetForm,
    onSuccess,
    onClose,
  ]);

  const handleCancelWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  const handleConfirmSave = useCallback(async () => {
    setShowWarning(false);
    setIsCreating(true);
    await performSave();
  }, [performSave]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      // In edit mode, show warning before saving
      if (isEditMode) {
        setShowWarning(true);
        return;
      }

      // In create mode, proceed directly
      setIsCreating(true);
      await performSave();
    },
    [validateForm, isEditMode, performSave]
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isEditMode ? "Edit Workflow" : "Create New Workflow"}
          </h2>
          {!isCreating && (
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {isCreating ? (
            /* Creation/Update Progress */
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {isEditMode ? "Updating Workflow..." : "Creating Workflow..."}
              </h3>

              <div className="space-y-4">
                {creationSteps.map((step) => (
                  <div key={step.id} className="flex items-center space-x-3">
                    {step.status === "pending" && (
                      <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                    )}
                    {step.status === "in_progress" && (
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    )}
                    {step.status === "completed" && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    {step.status === "error" && (
                      <AlertCircle className="w-5 h-5 text-red-500" />
                    )}

                    <span
                      className={`text-sm ${
                        step.status === "completed"
                          ? "text-green-600 dark:text-green-400"
                          : step.status === "error"
                          ? "text-red-600 dark:text-red-400"
                          : step.status === "in_progress"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>

              {errors.submit && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                  <div className="flex">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                        Creation Failed
                      </h3>
                      <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                        {errors.submit}
                      </p>
                      <button
                        onClick={() => setIsCreating(false)}
                        className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* SWRM-128: Two-Column Layout - Pipeline Selector + Workflow Creator */
            <form onSubmit={handleSubmit} className="flex flex-col h-full">
              {/* Two-column layout section */}
              <div className="flex h-[60vh] border-b border-gray-200 dark:border-gray-700">
                {/* LEFT COLUMN: Pipeline List */}
                <div className="w-64 border-r border-gray-200 dark:border-gray-700 flex flex-col flex-shrink-0">
                  <div className="p-4 pb-2 flex-shrink-0">
                    <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">
                      Select Pipeline *
                    </h3>

                    {/* Search filter */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search pipelines..."
                        value={pipelineSearchQuery}
                        onChange={(e) => setPipelineSearchQuery(e.target.value)}
                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                                 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {loadingPipelines ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Loading pipelines...
                      </div>
                    ) : pipelinesError ? (
                      <div className="text-sm text-red-600 dark:text-red-400">
                        {pipelinesError}
                      </div>
                    ) : filteredPipelines.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {pipelineSearchQuery
                          ? "No pipelines match your search"
                          : "No active pipelines available"}
                      </div>
                    ) : (
                      filteredPipelines.map((pipeline) => (
                        <button
                          key={pipeline.id}
                          type="button"
                          onClick={() =>
                            !isEditMode &&
                            setFormData({
                              ...formData,
                              pipelineId: pipeline.id,
                            })
                          }
                          disabled={isEditMode}
                          className={`w-full text-left px-2.5 py-2 rounded mb-1 transition-colors ${
                            formData.pipelineId === pipeline.id
                              ? "bg-blue-100 dark:bg-blue-900 border-2 border-blue-500"
                              : isEditMode
                              ? "bg-gray-50 dark:bg-gray-800 border-2 border-transparent opacity-50 cursor-not-allowed"
                              : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent"
                          }`}
                        >
                          <div className="font-medium text-sm text-gray-900 dark:text-white">
                            {pipeline.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {pipeline.description || "No description"}
                          </div>
                        </button>
                      ))
                    )}

                    {errors.pipelineId && (
                      <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                        {errors.pipelineId}
                      </p>
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN: Workflow Configuration */}
                <div className="flex-1 overflow-y-auto p-6">
                  {!formData.pipelineId ? (
                    <div className="text-center text-gray-500 dark:text-gray-400 mt-20">
                      <p>Select a pipeline from the left to continue</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Selected Pipeline Info */}
                      {selectedPipeline && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {selectedPipeline.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {selectedPipeline.description}
                          </p>
                        </div>
                      )}

                      <hr className="border-gray-200 dark:border-gray-700" />

                      {/* Workflow Configuration (Name + Priority) */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          Workflow Configuration
                        </h4>

                        <div>
                          <label
                            htmlFor="workflow-name"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            Workflow Name *
                          </label>
                          <input
                            id="workflow-name"
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                              handleInputChange("name", e.target.value)
                            }
                            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                              errors.name ? "border-red-500" : "border-gray-300"
                            }`}
                            placeholder="e.g., User Authentication System"
                          />
                          {errors.name && (
                            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="priority"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                          >
                            Priority
                          </label>
                          <select
                            id="priority"
                            value={formData.priority}
                            onChange={(e) =>
                              handleInputChange("priority", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          >
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        </div>

                        {/* ADR-017: Group selector for workflow scoping */}
                        {userGroups.length > 1 && (
                          <div>
                            <label
                              htmlFor="group"
                              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                            >
                              Run in Group *
                            </label>
                            <select
                              id="group"
                              value={formData.groupId || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  groupId: e.target.value || null,
                                }))
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            >
                              <option value="">Select a group...</option>
                              {userGroups.map((g) => (
                                <option key={g.id} value={g.id}>
                                  {g.name}
                                </option>
                              ))}
                            </select>
                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              Workflow results will be scoped to this group
                            </p>
                          </div>
                        )}
                        {userGroups.length === 1 && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Group
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {userGroups[0].name}
                            </p>
                          </div>
                        )}
                      </div>

                      <hr className="border-gray-200 dark:border-gray-700" />

                      {/* SWRM-128: Dynamic Processor Inputs (ALL processors with input_definitions) */}
                      {Object.entries(allProcessorInputs).map(
                        ([processorName, inputData], index) => (
                          <div key={processorName} className="space-y-4">
                            <h4 className="font-medium text-gray-900 dark:text-white capitalize">
                              {processorName === "input"
                                ? "Pipeline Inputs"
                                : `${processorName} Configuration`}
                            </h4>
                            <DynamicWorkflowInputs
                              inputs={inputData.definitions}
                              values={inputData.values}
                              errors={inputData.errors}
                              onChange={(name, value) => {
                                setAllProcessorInputs((prev) => ({
                                  ...prev,
                                  [processorName]: {
                                    ...prev[processorName],
                                    values: {
                                      ...prev[processorName].values,
                                      [name]: value,
                                    },
                                  },
                                }));
                              }}
                              onError={(name, error) => {
                                setAllProcessorInputs((prev) => ({
                                  ...prev,
                                  [processorName]: {
                                    ...prev[processorName],
                                    errors: {
                                      ...prev[processorName].errors,
                                      [name]: error,
                                    },
                                  },
                                }));
                              }}
                            />
                            {index <
                              Object.keys(allProcessorInputs).length - 1 && (
                              <hr className="border-gray-200 dark:border-gray-700 my-6" />
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
                {/* End of two-column layout */}
              </div>

              {/* Actions - Fixed at bottom outside two-column layout */}
              <div className="p-4 flex justify-end space-x-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !formData.pipelineId}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEditMode ? "Save Changes" : "Create Workflow"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Warning Dialog for Edit Mode */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Reset Workflow?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Saving these changes will reset the workflow and clear all
                  existing execution progress. This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelWarning}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmSave}
                    className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    Yes, Save & Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
